<script setup>
import "flatpickr/dist/flatpickr.css";
</script>

<template>
  <div class="card">
    <div class="card-header pb-0">
      <h6>Limit Reward</h6>
    </div>

    <!-- 🔍 Filter -->
    <div class="row p-3 g-2">
      <div class="col-12 col-md-3">
        <input
          type="text"
          class="form-control"
          v-model="searchQuery"
          placeholder="ค้นหา Username / ID / Game Code"
        />
      </div>

      <div class="col-6 col-md-2">
        <input type="date" class="form-control" v-model="startDate" />
      </div>

      <div class="col-6 col-md-2">
        <input type="date" class="form-control" v-model="endDate" />
      </div>

      <div class="col-6 col-md-2">
        <button class="btn btn-primary w-100" @click="getData">
          ค้นหา
        </button>
      </div>

      <div class="col-6 col-md-2">
        <button class="btn btn-secondary w-100" @click="resetFilter">
          Reset
        </button>
      </div>
    </div>

    <!-- 🔄 Loading -->
    <div v-if="isLoading" class="text-center p-4">
      Loading...
    </div>

    <!-- 📊 Table -->
    <div v-else class="card-body px-0 pt-0 pb-2">
      <div class="table-responsive p-0">
        <table class="table align-items-center mb-0">
          <thead>
            <tr>
              <th
                v-for="column in columns"
                :key="column.id"
                class="text-uppercase text-xxs font-weight-bolder ps-2 cursor-pointer"
                @click="sortBy(column.id)"
              >
                {{ column.name }}
                <span v-if="sortKey === column.id">
                  {{ sortOrder === "asc" ? " 🔼" : " 🔽" }}
                </span>
              </th>
            </tr>
          </thead>

          <tbody>
            <!-- ❌ ไม่มีข้อมูล -->
            <tr v-if="sortedData.length === 0">
              <td :colspan="columns.length" class="text-center py-4">
                ไม่มีข้อมูล
              </td>
            </tr>

            <!-- ✅ มีข้อมูล -->
            <tr v-for="(item, index) in sortedData" :key="index">
              <td v-for="column in columns" :key="column.id">
                {{ itemValue(item, column.id) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 📄 Pagination -->
      <div class="d-flex justify-content-center mt-3 flex-wrap">
        <button
          class="btn btn-sm btn-secondary m-1"
          @click="previousPage"
          :disabled="currentPage === 1"
        >
          Prev
        </button>

        <span class="m-2">
          Page {{ currentPage }} / {{ totalPages }}
        </span>

        <button
          class="btn btn-sm btn-secondary m-1"
          @click="nextPage"
          :disabled="currentPage === totalPages"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "LimitRewardTable",

  data() {
    return {
      searchQuery: "",
      startDate: "",
      endDate: "",

      data: [],
      isLoading: false,

      currentPage: 1,
      rowsPerPage: 50,
      totalPages: 1,

      // 🔽 sort
      sortKey: "",
      sortOrder: "asc",

      columns: [
        { id: "transaction_id", name: "Transaction ID" },
        { id: "username", name: "Username" },
        { id: "game_code", name: "Game Code" },
        { id: "bet_amount", name: "Bet Amount" },
        { id: "reward_before", name: "Reward Before" },
        { id: "reward_after", name: "Reward After" },
        { id: "updated_date", name: "วันที่" },
        { id: "updated_time", name: "เวลา" },
        { id: "created_by", name: "สร้างโดย" },
      ],
    };
  },

  created() {
    this.setToday();
    this.getData();
  },

  computed: {
    sortedData() {
      if (!this.sortKey) return this.data;

      return [...this.data].sort((a, b) => {
        let valA = a[this.sortKey];
        let valB = b[this.sortKey];

        if (!isNaN(valA) && !isNaN(valB)) {
          valA = Number(valA);
          valB = Number(valB);
        } else {
          valA = valA?.toString().toLowerCase();
          valB = valB?.toString().toLowerCase();
        }

        if (valA < valB) return this.sortOrder === "asc" ? -1 : 1;
        if (valA > valB) return this.sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    },
  },

  methods: {
    setToday() {
      const today = new Date();
      const pad = (n) => n.toString().padStart(2, "0");

      const formatted = `${today.getFullYear()}-${pad(
        today.getMonth() + 1
      )}-${pad(today.getDate())}`;

      this.startDate = formatted;
      this.endDate = formatted;
    },

    async getData() {
      try {
        this.isLoading = true;

        const url = "https://dashboard-api.linkstudio.io/api/v1/game/control/limit-reward";

        const params = {
          page: this.currentPage,
          limit: this.rowsPerPage,
        };

        if (this.startDate && this.endDate) {
          params.startDate = this.startDate;
          params.endDate = this.endDate;
        }

        if (this.searchQuery) {
          const query = this.searchQuery.trim();

          if (query.length == 4 && query.startsWith("G")) {
            params.game_code = query;
          } else if (query.length > 10) {
            params.transaction_id = query;
          } else {
            params.username = query;
          }
        }

        const response = await axios.get(url, { params });

        this.data = response.data.data.list;
        this.totalPages = response.data.data.totalPages;

        this.isLoading = false;
      } catch (error) {
        console.error(error);
        this.isLoading = false;
      }
    },

    resetFilter() {
      this.searchQuery = "";
      this.setToday();
      this.currentPage = 1;
      this.sortKey = "";
      this.sortOrder = "asc";
      this.getData();
    },

    sortBy(column) {
      if (this.sortKey === column) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = column;
        this.sortOrder = "asc";
      }
    },

    itemValue(item, columnId) {
      return item[columnId];
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.getData();
      }
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.getData();
      }
    },
  },
};
</script>

<style scoped>
.table td {
  white-space: nowrap;
  font-size: 12px;
}

.cursor-pointer {
  cursor: pointer;
}

@media (max-width: 768px) {
  .table {
    font-size: 12px;
  }

  .card-header h6 {
    font-size: 14px;
  }
}
</style>